/** 도메인 상태값과 실패 코드. **문자열이 apps/api 의 실제 값과 일치해야 한다** —
 * D1 컬럼의 CHECK 제약(`migrations/0002_ownership_billing_history.sql`,
 * `migrations/0004_export_size_settings_notification.sql`)과 이 파일이 정본을 나눠 갖는다.
 * 어느 한쪽만 고치면 INSERT 가 런타임에 거절된다.
 *
 * 앱·웹이 기능 동등이므로(PRD 3.) 이 파일은 양쪽이 공유한다. 화면 코드에 상태 문자열을
 * 다시 적지 않는다 — 한쪽만 고치면 두 UI 가 다르게 판단한다.
 */

import type { ChannelSpecVersion, SalesChannel } from "./channel";
// ⛔ **`import type` 이어야 한다.** 그쪽 파일이 이 파일의 `ActivitySource` 를 다시 쓰고 있어
// 값으로 가져오면 순환이 런타임으로 내려간다. 타입은 컴파일에서 지워져 고리가 안 생긴다.
import type { ProductCategoryStatementRecord } from "./product-category-statement";
// ⚠️ 위 줄과 붙여 둔다. **타입만 가져오는 순환**이고 위 줄이 이미 같은 모양이다(양쪽 파일이
// `ActivitySource` 를 이 파일에서 가져간다). ⛔ **값을 가져오지 마라.** 순환이 실행 순서를
// 갖는 순간 한쪽이 초기화 전에 읽힌다.
import type { ProductClassificationRecord } from "./product-classification";
import type { D1DateTime } from "./retention";
import type { ResultReceiveView } from "./result-retention";

/** 상세 생성 작업의 수명주기. 상용 API 호출이 비동기라 상태를 서버가 들고 있어야 한다.
 *
 * 0002 가 셋을 늘렸다. 칩 라벨은 `job-labels.ts` 가 정본이다(PRD 7.5.8):
 * - `PAID`      — 결제됐고 자동 시작 전. 방치가 아니라 처리 확약 구간이다(PRD S3b ②)
 * - `EXPORTED`  — 내보냈다. 목업 A-10 의 `내보냄` 칩이 여기 붙는다
 * - `DISCARDED` — 결제 전 그만둬 원본을 파기했다. **행은 남는다** — 흔적 없이 사라지면
 *   셀러가 "내가 올린 게 어디 갔지"를 겪는다(PRD 5. ①)
 *
 * ⚠️ 값을 늘리면 `job-rules.ts`·`job-labels.ts` 를 함께 고쳐야 한다. 문자열 유니온이라
 * `default` 가 달린 곳은 컴파일 에러 없이 조용히 빠진다. */
export type DetailJobStatus =
  | "UPLOADED"
  | "PAID"
  | "PROCESSING"
  | "READY"
  | "EXPORTED"
  | "FAILED"
  | "DISCARDED";

/** 더 이상 서버가 건드리지 않는 상태. 폴링을 멈추는 기준이고 `job-rules.ts` 가 읽는다. */
export const TERMINAL_JOB_STATUSES = ["READY", "EXPORTED", "FAILED", "DISCARDED"] as const;

/** 이미지 한 장의 처리 상태. 일부만 실패할 수 있어 작업 상태와 따로 둔다 —
 *  8장 중 1장이 실패했을 때 전체를 FAILED 로 만들면 나머지 7장의 결과를 버리게 된다.
 *
 *  0002 가 `PROCESSING` 을 더했다. 이 값이 없으면 목업 A-24 의 `처리` 칩을 서버 데이터로
 *  만들 수 없고, 처리 중인 장이 `PENDING` 으로 보여 이중 실행 판정이 흔들린다. */
export type JobImageStatus = "PENDING" | "PROCESSING" | "DONE" | "FAILED";

/** 서버가 내려보내는 비민감 실패 코드. **이 표가 곧 청구 계약이다**(PRD S3b).
 *
 * 장 레벨 — provider 호출 **전에** 끊긴 것 (원가 0, 실패율 통계 제외):
 * - `NOT_CONFIGURED`   — provider 키 없음 (503)
 * - `SOURCE_MISSING`   — 원본 R2 객체가 없어 보낼 것이 없었음
 * - `SOURCE_TOO_LARGE` — 상한 초과로 호출 전에 끊음. upload.ts 의 `TOO_LARGE`(올리기 전
 *   클라이언트 차단)와 축이 다르므로 이름을 갈랐다. PRD S3b 표의 행 이름도 고쳐야 한다.
 *
 * 장 레벨 — provider 가 오류 응답을 준 것 (Photoroom 은 오류에 미과금 [공식]):
 * - `PROVIDER_REJECTED` — 4xx. 이 사진을 거절했다
 * - `PROVIDER_FAILED`   — 5xx·네트워크. 호출이 실패했다
 *
 * 장 레벨 — **공급자가 아니라 우리 쪽이 끊긴 것**:
 * - `PROCESSING_INTERRUPTED` — 호출은 나갔는데 **응답을 받은 적이 없다.** 워커 실행이 통째로
 *   끝나 `markImageDone` 도 `markImageFailed` 도 돌지 못한 장이다. **`PROVIDER_FAILED` 로
 *   적지 않는 이유가 귀책이다** — 그쪽은 "공급자가 실패했다"는 단정이고, 이 장은 공급자가
 *   성공했는지조차 모른다. 뭉개면 **우리 런타임 크래시가 공급자 실패율로 집계되고**, 그
 *   숫자는 공급자를 바꿀지 판단하는 재료다(`docs/DATA_DICTIONARY.md` 19.4).
 *   ⚠️ `PRE_CALL_ERROR_CODES` 에 **넣지 않는다.** 호출은 실제로 나갔고 원가도 나갔다.
 *
 * 작업 레벨 전용:
 * - `PARTIAL_FAILURE` — 일부 실패. 쓸 수 있는 결과가 있다
 * - `TOTAL_FAILURE`   — 한 장도 못 만들었다. 전액 환불 대상
 *
 * ⚠️ 전량 실패에 `PARTIAL_FAILURE` 를 붙이지 마라 — 전액 환불 대상 셀러가 "만들어진 장은
 * 그대로 쓸 수 있습니다"를 읽게 된다. 두 코드를 가른 이유가 이것이다.
 *
 * 서버가 새 코드를 내려도 화면이 깨지면 안 되므로 `(string & {})` 를 열어둔다. */
export type JobFailureCode =
  | "NOT_CONFIGURED"
  | "SOURCE_MISSING"
  | "SOURCE_TOO_LARGE"
  | "PROVIDER_REJECTED"
  | "PROVIDER_FAILED"
  | "PROCESSING_INTERRUPTED"
  | "PARTIAL_FAILURE"
  | "TOTAL_FAILURE"
  // eslint-disable-next-line @typescript-eslint/ban-types
  | (string & {});

/** provider 를 부르기 전에 끊긴 코드. 원가가 0 이라 실패율·단가 통계에서 제외한다. */
export const PRE_CALL_ERROR_CODES = [
  "NOT_CONFIGURED",
  "SOURCE_MISSING",
  "SOURCE_TOO_LARGE",
] as const;

/** 지금 공급자를 다시 부를 수 **없는** 이유. `null` 이면 부를 수 있다.
 *  (`docs/DATA_DICTIONARY.md` 28.5 ③. 반영 근거는 28.8)
 *
 * **`failure-messages.ts` 의 `retryable` 과 다른 축이다.** 그쪽은 **사진 축**("같은 사진으로
 * 다시 걸면 결과가 달라지는가")이고 이쪽은 **우리 축**("지금 부를 수 있는가")이다. 두 축을 한
 * 칸에 겹쳐 둔 것이 사고의 원인이었다. `NOT_CONFIGURED` 가 `retryable: true` 라서 화면이
 * **우리 설정이 막힌 장에 ₩500 재결제 시트를 열었다**(PRD `7.6.8a` · `7.13` D-77 ②).
 * ⛔ 그렇다고 `retryable` 을 뒤집지 않는다. 그 값은 사진 축에서 옳다.
 *
 * **정본은 서버다.** 공급자가 지금 살아 있는지를 화면은 알 수 없다. 서버가 이 값을 채우기
 * 전까지 쓰는 폴백은 `job-rules.ts` 의 `recallBlockOf` **한 곳에만** 둔다.
 *
 * ⛔ **아래는 씨앗이고 닫힌 목록이 아니다.** 그래서 `(string & {})` 를 열어 둔다
 * (`JobFailureCode` 와 같은 이유). 서버가 새 사유를 내려도 옛 화면이 안 깨진다.
 *
 * ⛔ **`JobFailureCode` 를 그대로 베껴 채우지 마라.** 이름이 겹치는 값이 있어도 두 칸이
 * 답하는 질문이 다르다. 이쪽은 **지금 이 순간** 그 사진을 보낼 수 있는가이고, **만든 장**
 * (`DONE`. `errorCode` 가 `null` 인 장)의 다시 만들기에도 붙는다. */
export type RecallBlockReason =
  /** 우리 쪽이 막혔다. 지금 부르면 또 실패한다(`NOT_CONFIGURED` 계열). **돈을 받지 않는다.**
   *  기다리면 풀리는 갈래다(`A-25c` ⓘ). */
  | "PROVIDER_UNAVAILABLE"
  /** 원본 R2 객체가 없다. 부를 밑천이 없어 다시 올려야 한다(`SOURCE_MISSING`).
   *  그 사진으로는 영영인 갈래다(`A-25c` ⓘⓘ). */
  | "SOURCE_GONE"
  /** 원본은 있는데 **처리할 수 있는 크기를 넘는다**(`SOURCE_TOO_LARGE`). 역시 ⓘⓘ 이고,
   *  `SOURCE_GONE` 과 **한 값으로 뭉치지 않는다**. 아래 판정 1 이 근거다. */
  | "SOURCE_TOO_LARGE"
  /** 그 작업이 지금 돌고 있다. 서버가 409(`ALREADY_PROCESSING`)를 준다.
   *  ⛔ **막힘이 아니라 바쁨이라 `A-25c` 의 사유가 아니다.** 아래 판정 2. */
  | "JOB_PROCESSING"
  /** 원본이 파기됐다(`DISCARDED`). 장이 아니라 작업 전체에 걸린다. */
  | "JOB_DISCARDED"
  /** ⭐ **그 장의 원본 보관 기한(30일)이 지났다**(`0011` · `DATA_DICTIONARY` 31.8).
   *
   * ⛔ **`SOURCE_GONE` 과 뭉치지 마라. 두 값이 답하는 것이 다르다.** `SOURCE_GONE` 은
   * **객체가 없다**는 관측이고 이쪽은 **기한이 지났다**는 판정이다. 배치가 도는 것은 하루
   * 한 번이라 **기한은 지났는데 객체는 아직 있는 구간이 매일 생긴다.** 그 구간에서
   * `SOURCE_GONE` 으로 적으면 「사진이 없다」고 말하게 되는데 사진은 있다.
   * ⭐ **참인 문장은 「기한이 지났다」 하나뿐이다.**
   *
   * 성격은 `SOURCE_GONE` 과 같은 `TERMINAL` 이다. 기다려서 풀리지 않고 그 사진으로는
   * 영영이다. ⛔ **`다시 되는 때` 줄을 세우지 마라.**
   *
   * ⚠️ **문장은 이 값이 고르지 않는다.** `design-manager` 소관이고 `docs/PRD.md` `7.6.8a`
   * 표에 이 갈래의 줄이 아직 없다(32.9 남긴 일). 그때까지 화면은 `UNKNOWN` 이 아니라
   * `TERMINAL` 로 받으므로 **오지 않을 날을 기다리게 하지는 않는다.** */
  | "RETENTION_EXPIRED"
  // eslint-disable-next-line @typescript-eslint/ban-types
  | (string & {});

/* ⭐ **판정 1 (2026-09-21). `SOURCE_GONE` 과 `SOURCE_TOO_LARGE` 를 한 값으로 뭉치지 않는다.**
 *
 * `design-manager` 가 `A-25c` 갈래 ⓘⓘ 의 문장을 **둘로 갈랐다**(PRD `7.6.8a`).
 * `SOURCE_MISSING` 은 `같은 상품을 다시 찍어 새로 올려 주세요`, `SOURCE_TOO_LARGE` 는
 * `더 작게 찍거나 줄여서 새로 올려 주세요` 다. 사유 줄도 갈린다. 한 값으로 뭉치면 화면이
 * 그 둘을 못 가른다.
 *
 * **그러면 화면이 `errorCode` 로 문장을 고르게 두면 되지 않는가. 안 된다. 갈래 둘 때문이다.**
 *   ① **만든 장의 다시 만들기.** `DONE` 인 장은 `errorCode` 가 `null` 인데, 원본이 없거나
 *      규격을 넘으면 **그 장도 막힌다.** 그때 문장을 고를 입력이 없다.
 *   ② **두 칸이 같은 사건을 가리키지 않는다.** `PROVIDER_REJECTED` 로 실패한 장의 원본이
 *      나중에 사라지면 `errorCode` 는 `PROVIDER_REJECTED` 인데 막는 것은 원본이다.
 *      `errorCode` 로 문장을 고르면 **색 대비 이야기를 하고 ⓘⓘ 문장은 안 나온다.**
 * **즉 `errorCode` 에 기대는 순간 28.5 ③ 이 없애려던 축 겹침이 다시 생긴다.**
 *
 * ⚠️ **값을 더해도 열거를 얼리는 것이 아니다.** `(string & {})` 가 열려 있고, D-81 ⓐ 가 이 두
 * 코드의 **시트**를 다시 정하면 바뀌는 것은 화면이지 이 값이 아니다. 이 칸이 답하는 것은
 * "무엇이 막는가" 하나이고 그것은 이미 참이다.
 *
 * ⚠️ **판정 2 (2026-09-21). `JOB_PROCESSING` 은 값으로 두되 `A-25c` 를 열지 않는다.**
 *
 * **두 에이전트가 이 한 점에서 갈렸다.** `design-manager` 는 「이 작업의 다른 장이 도는 중」을
 * `A-25c` ⓘ 의 사유로 표에 넣었고(PRD `7.6.8a`), `prd-manager` 는 **"막힘이 아니라 바쁨이라
 * `다시 되는 때 / 미정` 이 거짓이 된다. 동시 실행을 막아야 한다면 그것은 버튼 잠금이지 이
 * 시트가 아니다"** 로 뺐다(PRD `7.13` D-77 ②).
 *
 * **`prd-manager` 쪽이 맞다. 근거는 이 파일 아래의 코드다.** `canRemakeImage` ·
 * `canRetryImage` 가 **처리 중이면 이미 `false`** 를 돌려 **버튼이 잠기고, 잠긴 버튼에서는
 * 시트가 열리지 않는다.** 그 상태를 시트 사유로도 두면 **같은 사실을 두 자리에서 판정하게
 * 되고**, 그중 하나는 셀러에게 `다시 되는 때 / 미정` 이라고 적는다. **몇 초 뒤 끝날 일에
 * "언제 될지 모른다"이다.**
 *
 * **그래서 값은 남기고 파생만 막았다.** 값이 남는 이유는 이 칸이 답하는 질문("지금 부를 수
 * 있는가")에 대해 **참이기 때문**이다. 서버가 409 를 주는 상태를 `null`(=부를 수 있다)로
 * 적으면 그 칸이 거짓말을 한다. `recallBlockOf` 가 **처리 중을 사유로 파생하지 않고**,
 * `recallBlockKind` 가 이 값만 `BUSY` 로 갈라 시트 축에서 떼어낸다. */

/** **못 만든 장을 두고 셀러가 고른 둘 중 하나**(`A-24e` 시트의 `seg`. PRD `7.6.7d`).
 *  (`docs/DATA_DICTIONARY.md` 34장)
 *
 * **셀러가 보는 낱말은 `다시 만들기` / `만들지 않기` 다.** 이 값은 그 낱말이 아니라 **서버가
 * 받는 동작**이고, 둘의 이름이 다른 것은 일부러다 — 화면 낱말은 `design-manager` 가 고치고
 * (PRD `7.5.9`) 이 열거는 요청 본문의 계약이라 **고치면 배포가 갈린다.**
 *
 * ⛔ **`RecallBlockReason` 과 축이 다르다.** 그쪽은 **우리가 막은 것**이고 이쪽은 **셀러가
 * 고른 것**이다. 한쪽이 다른 쪽을 대신 답하지 않는다 — 막힌 장(`SOURCE_GONE`)에도
 * `DECLINE` 은 열려 있어야 한다. **다시 만들 수 없는 장이야말로 안 만들기로 정할 장이다.**
 *
 * ⚠️ **`RECALL` 은 대기로 되돌리는 것까지다. 공급자를 부르지 않는다.** 시트가
 * `어느 쪽을 고르셔도 지금 나가는 돈은 없습니다` 라고 적는데(PRD `7.6.7d`), 고르는 순간
 * 호출이 나가면 그 문장이 거짓이 된다. **부르는 것은 그 뒤 `만들기 시작`(`process`)이다.** */
export type RecallDecision =
  /** **`다시 만들기`.** 못 만든 장을 `대기` 로 되돌린다(`FAILED` → `PENDING`). 그 장은 다음
   *  회차의 처리 대상이 되고, **그때부터는 `아직 만들지 않은 장`이라 이 물음에서 빠진다.**
   *  ⭐ **그래서 이 쪽은 저장할 칸이 없다 — 장 상태가 곧 기록이다**(32.2). */
  | "RECALL"
  /** **`만들지 않기`.** 그 장을 더 만들지 않기로 결판낸다. `JobImage.recallDeclinedAt` 이
   *  박힌다. ⛔ **환불 실행은 이 값이 하는 일이 아니다**(32.6. 결제사 미정, PG 호출 0건). */
  | "DECLINE";

// ── 출처 · 분류 ──────────────────────────────────────────────────────────────

/** 앱·웹·서버. 이력 한 줄마다 반드시 붙는다(PRD 7.9). */
export type ActivitySource = "APP" | "WEB" | "SERVER";

/** 작업을 처음 만든 곳. `UNKNOWN` 은 0002 이전 행 전용이다. */
export type JobOrigin = "APP" | "WEB" | "UNKNOWN";

/** 사진이 들어온 길. 이력이 촬영분과 앨범분을 나눠 센다. */
export type CaptureSource = "CAMERA" | "LIBRARY" | "FILE" | "UNKNOWN";

/** 산출물 블록 타입. 정본은 `docs/TEMPLATE.md` 3장이라 DB 는 CHECK 로 못박지 않는다. */
export type BlockType = "hero" | "gallery" | "detail-crop" | "spec" | "notice";

/** 누끼 하나에서 파생된 **축소본의 종류**. `JobImageRendition.kind` 와 짝이다
 *  (`docs/DATA_DICTIONARY.md` 24장 · M-18).
 *
 * 누끼 원본은 3024×3350 RGBA 라 디코드에 41MB 를 먹어 앱이 못 그린다(24.1). 원본은
 * `detail-crop` 이 쓰므로 줄이지 않고, **화면에 그릴 작은 그림을 따로 만들어 둔다.**
 *
 * - `PREVIEW` . 한 장을 크게 보는 자리(상세·편집·다시 만들기 확인)에 그리는 축소본.
 *   **긴 변 1280px**
 * - `THUMB` . 여러 장을 한 화면에 늘어놓는 자리(큐·그리드)에 그리는 더 작은 것.
 *   **긴 변 320px**
 *
 * ✅ **둘 다 만든다** (2026-09-19 `design-manager` 확정. 정본은 `docs/PRD.md` **7.5.14**).
 * 이 주석의 앞 판은 `THUMB` 를 "낱말만 잡아 둔 것이고 아직 만들지 않는다"고 적었는데,
 * 같은 날 판이 둘로 확정됐다. **하나로 줄이지 않는 근거가 양쪽에 있다**: 큰 판으로 통일하면
 * 목록이 `8 × 5.9MB` 를 펴서 지금 고치는 버그가 작아진 채로 돌아오고, 작은 판으로 통일하면
 * `A-51` 결과 확인이 누끼 가장자리를 판정하지 못해 **장당 ₩500 을 쓸지의 판단이 흐려진다.**
 *
 * ⚠️ **픽셀 값의 정본은 그 절이지 여기가 아니다.** 위 320·1280 은 읽는 사람을 위해 적어 둔
 * 것이라 그 절과 어긋나는 날은 **그 절이 맞다.** 형식도 그쪽이 정한다(지금은 PNG · RGBA 8비트).
 *
 * ⛔ **이것을 "판"이라고 부르지 마라.** 이 제품에서 판은 `JobVersion`(내보낸 판, `현재 v2`)
 * 이다. 같은 낱말로 부르면 `versionNo` 와 이 값이 화면에서 섞인다(24.2).
 *
 * ⚠️ **DB 에 CHECK 를 걸지 않는다.** 값이 늘어날 줄이고, SQLite 는 CHECK 를 `ALTER TABLE`
 * 로 못 고쳐 테이블 재작성이 된다(11장). `BlockType`·`JobEvent.type` 과 같은 이유이고
 * **이 유니온이 정본이다.** */
export type JobImageRenditionKind = "PREVIEW" | "THUMB";

/** 로그인 수단. **정본 목업 `A-01`·`W-01` 의 버튼 세 개와 1:1 이다.**
 *
 * `KAKAO` 가 0006 에서 들어왔다. 목업은 2026-09-08 에 이미 `카카오로 시작하기` 를 그려
 * 뒀는데 이 유니온이 둘뿐이라 그 버튼이 꺼진 채로 있었다. **코드가 목업에서 벗어나 있던
 * 것이지 목업이 앞서간 것이 아니다.** 같은 화면의 캡션이 근거를 적어 뒀다. 스마트스토어는
 * 네이버 계정 없이 운영할 수 없어 지금 타깃에는 카카오로 새로 닿는 셀러가 없지만, **쿠팡만
 * 하는 셀러는 네이버 계정을 가질 이유가 없다.** 나중에 붙이지 않고 지금 두는 이유는 계정
 * 정체성이 뒤로 갈수록 옮기기 비싸기 때문이다. 그때는 공급자를 늘리는 일이 아니라 **이미
 * 갈라진 계정을 합치는 일**이 된다.
 *
 * ⚠️ **이 유니온은 "서버가 값으로 받아들이는 것"이지 "지금 로그인 화면에서 도는 것"이
 * 아니다.** 뒤쪽은 `auth.ts` 의 `ENABLED_IDENTITY_PROVIDERS` 다. 둘이 다른 것이 맞다.
 *
 * ⚠️ 값을 늘리면 D1 의 CHECK 도 함께 넓혀야 한다. 두 자리다. `SellerIdentity.provider` 와
 * `AuthLoginAttempt.provider`. SQLite 는 CHECK 를 `ALTER TABLE` 로 못 고쳐 테이블 재작성이고,
 * 그 근거는 `docs/DATA_DICTIONARY.md` 11장과 `migrations/0006_identity_provider_kakao.sql`
 * 에 있다. **한쪽만 넓히면 INSERT 가 런타임에 거절된다.**
 *
 * ⚠️ **그리고 순서가 있다. 마이그레이션 적용이 배포보다 먼저다.** `lib/auth.ts` 의
 * `createLoginAttempt` 가 새 값을 `AuthLoginAttempt` 에 INSERT 하므로, CHECK 가 아직 좁은
 * DB 를 보는 배포에서는 그 버튼이 **인가 화면에도 못 가고 첫 클릭에서** CHECK 위반으로
 * 죽는다. 콜백이 아니라 시작에서 나는 실패다. 카카오는 이 순서를 지켜 넣었다
 * (`0006` 적용 → 라우트 → `ENABLED_IDENTITY_PROVIDERS`). 다음 공급자도 같다. */
export type IdentityProvider = "NAVER" | "APPLE" | "KAKAO";

// ── 결제 · 환불 ──────────────────────────────────────────────────────────────

/** 결제 상태. 칩은 `CAPTURED`=`완료` / `PARTIALLY_REFUNDED`=`일부 돌아감` /
 *  `REFUNDED`=`전부 돌아감` (PRD 7.5.9, `paymentStatusLabel`).
 *
 * **값 이름은 `REFUNDED` 로 두고 라벨만 표의 낱말로 돌려준다.** 값 이름은 DB CHECK 와 짝이라
 * 화면 문구를 따라 바꾸지 않는다 — `환불`은 여기서 개념어다. */
export type PaymentStatus =
  | "PENDING"
  | "AUTHORIZED"
  | "CAPTURED"
  | "PARTIALLY_REFUNDED"
  | "REFUNDED"
  | "FAILED"
  | "CANCELED";

/** 환불 실행 방식. **미정이라 양쪽을 담는다**(PRD 13.1 O-13) — 여기서 고르지 않는다. */
export type SettlementMode = "CAPTURE_THEN_REFUND" | "AUTH_THEN_PARTIAL_CAPTURE";

/** 환불 사유. `TOTAL_FAILURE`·`CANCELED_BEFORE_START` 는 전액, `PARTIAL_FAILURE` 는 차액.
 *  이름을 `JobFailureCode` 와 맞춰 뒀다 — 같은 사건을 두 이름으로 부르면 정산이 어긋난다. */
export type RefundReason =
  | "CANCELED_BEFORE_START"
  | "TOTAL_FAILURE"
  | "PARTIAL_FAILURE"
  | "MANUAL";

/** 환불이 실제 거래였는지, 매입을 덜 한 것인지. 실행 방식이 미정이라 둘 다 담는다. */
export type RefundMode = "PG_REFUND" | "CAPTURE_SHORTFALL";

/** 청구 항목 종류. 재시도는 새 호출이라 기존 항목 갱신이 아니라 새 항목이다. */
export type BillingItemKind = "CUTOUT" | "CUTOUT_RETRY";

// ── 판 · 동의 · 알림 ─────────────────────────────────────────────────────────

/** 판이 만들어지는 두 지점. 편집마다 만들지 않는다(PRD 7.13 D-3). */
export type VersionTrigger = "APP_APPROVAL" | "EXPORT";

/** 동의 문서 종류. `REFUND_POLICY` 필요 여부는 법무 검토 대상이라 값만 열어 둔다. */
export type ConsentDocType = "TERMS" | "PRIVACY" | "TRAINING_USE" | "REFUND_POLICY";

/** 알림 종류. `A-30` 이 그리는 다섯 종과 1:1 이다.
 *
 * `REFUNDED` 의 제목은 `못 만든 1장 금액이 돌아갔어요`(정본 목업 `A-30`. `돌려보냈어요` 는
 * PRD 7.5.9 가 금지한 말이다). 0002 의 CHECK 에는 이 값이 없어서
 * 환불 알림을 넣을 수 없었고 0004 가 더했다. **실패와 환불을 한 종류로 뭉개지 않는다.**
 * 셀러가 알아야 하는 것이 다르다. 실패는 다시 만들지, 환불은 돈이 돌아왔는지다. */
export type NotificationType = "COMPLETED" | "FAILED" | "PROGRESS" | "EXPORTED" | "REFUNDED";

// ── 화면에 내려가는 모양 ─────────────────────────────────────────────────────

export interface JobImageView {
  id: string;
  /** 셀러가 고른 순서. 도착 순서가 아니다 — 첫 장이 대표컷이다. */
  sortOrder: number;
  status: JobImageStatus;
  /** 실패 사유 코드. 비민감 값만 내려간다. */
  errorCode: JobFailureCode | null;
  /** status 가 DONE 일 때만 채워진다. API 기준 상대 경로다.
   *
   * ⛔ **이 주소를 앱 화면에 그대로 그리지 마라.** 이것이 가리키는 것은 누끼 원본이고
   * 실측 9.03MB · 3024×3350 RGBA 라 디코드에만 41MB 를 먹는다. 화면에 그릴 것은 아래
   * `previewUrl` 이고, 이 칸은 **내보내기·`detail-crop` 이 쓰는 원본**의 자리다(24.1). */
  outputUrl: string | null;
  /** 화면에 그리는 **축소본** 주소(`JobImageRenditionKind` 의 `PREVIEW`).
   *
   * `/api/jobs/<jobId>/images/<imageId>/output?v=<providerCallCount>&size=preview`
   *
   * ⭐ **경로를 나누지 않고 `outputUrl` 과 같은 자리에 `size` 칸을 더한다**
   * (2026-09-19 구현 확정. 근거는 `docs/PRD.md` 7.5.14 "구현에 넘기는 규칙").
   * `size` 가 없으면 지금까지와 똑같이 **원본**이다. `/preview` · `/thumb` 로 경로를 셋으로
   * 가르면 **`?v=` 를 붙이는 코드가 셋이 되고, 한쪽만 낡는 날 같은 장의 큰 그림과 작은
   * 그림이 서로 다른 회차를 가리킨다.** 그 사고를 막는 것이 `?v=` 의 존재 이유 전부라,
   * 그것을 지키는 장치를 셋으로 늘리지 않았다. 소유자 검사(`getJobForSeller`)와
   * `cache-control` 도 그래서 한 벌로 남는다.
   * ⛔ **모르는 `size` 값은 원본으로 흘려보내지 않는다.** 라우트가 `400 UNKNOWN_SIZE` 로
   * 끊는다 — 오타 하나가 41MB 짜리를 34pt 자리에 다시 꽂는 것을 막는다.
   *
   * ⚠️ **`null` 은 "이 장에 축소본이 없다"가 아니라 "아직 안 만들어졌다"이다.** 세 경우다.
   *   ① M-18 적용 전에 만들어진 장(**지금 쌓여 있는 전부**)
   *   ② 축소가 실패한 장. **누끼는 그때도 `DONE` 이다.** 축소 실패로 누끼 결과를 버리지
   *      않는 것이 불변식이고, 8장 중 1장 규칙(1장 2번)과 같은 성격이다
   *   ③ 다시 만든 뒤 축소가 아직 못 따라온 장. 낡은 축소본은 **주소를 주지 않는다**(24.6)
   *
   * ⚠️ **`null` 일 때 `outputUrl` 로 떨어지는 것은 지금 한시적으로만 살아 있다**
   * (2026-09-19. `docs/PRD.md` 7.5.14 · `DATA_DICTIONARY` 24.6c). 원래 이 자리는 ⛔ 였고
   * 이유는 그 폴백이 **지금 앱을 죽이는 그림 그 자체**여서다. 그런데 **이미 쌓여 있는 장에는
   * 축소본이 하나도 없어서**, 지금 끊으면 만들어 둔 작업의 그림이 통째로 사라진다. 그래서
   * 순서를 정해 두고 살려 뒀다: 작은 자리는 `thumbUrl` → `previewUrl` → `outputUrl`,
   * 큰 자리는 `previewUrl` → `outputUrl`(320px 를 404pt 로 늘리지 않는다).
   * ⛔ **이것은 규약이 아니라 이행기다. 걷는 조건은 축소본 백필**이고, 백필이 돌고 나면
   * 마지막 줄을 지운다. **데이터 쪽의 답은 폴백이 아니라 백필이다**(24.6c · 24.11).
   * 자리를 비우든 자리표시를 그리든은 그때도 화면 쪽 판단(`design-manager`)이다.
   *
   * ⚠️ **`?v=` 를 빼지 마라.** `outputUrl` 과 같은 이유다. 주소가 `jobId`·`imageId` 로만
   * 만들어져 다시 만들어도 글자가 그대로인데 전달 라우트가 `private, max-age=300` 을 붙인다.
   * 빼면 셀러가 돈을 내고 다시 만든 뒤 5분 동안 옛 그림을 본다. 값은 `outputUrl` 과 **같은
   * `providerCallCount`** 여야 한다. 두 주소가 다른 번호를 달면 같은 장의 큰 그림과 작은
   * 그림이 서로 다른 회차를 가리킨다. */
  previewUrl: string | null;
  /** 여러 장을 늘어놓는 자리에 그리는 **작은 그림** 주소(`JobImageRenditionKind` 의 `THUMB`).
   *
   * **`previewUrl` 과 규약이 같다.** 같은 라우트에 `size` 만 다르다
   * (`/api/jobs/<jobId>/images/<imageId>/output?v=<providerCallCount>&size=thumb`), 같은
   * `?v=`, 같은 세 가지 `null` 갈래, 원본에 기대지 말라는 같은 방향. **위 주석을 여기
   * 복사하지 않는다** — 두 벌이 되면 한쪽만 고쳐진다.
   *
   * **어느 자리가 어느 판을 쓰는가는 `docs/PRD.md` 7.5.14 가 정본이다.** 한 줄로 줄이면
   * **여러 장을 늘어놓으면 `thumbUrl`, 한 장을 크게 보면 `previewUrl`** 이다.
   *
   * ⛔ **둘은 서로를 대신하지 못한다. 어느 쪽도 다른 쪽의 폴백이 아니다.** 이것은 **둘 다
   * 있을 때 무엇을 고르느냐**의 규약이다. 이행기의 떨어지는 순서(`thumbUrl` →
   * `previewUrl` → `outputUrl`)는 **셋 다 없을 때 덜 나쁜 것을 고르는 것**이라 이 규약을
   * 뒤집지 않는다. ⚠️ 그래도 그 사이에는 목록이 큰 판을 펼 수 있다는 뜻이고, **그 상태를
   * 끝내는 것이 백필이다**(24.6c).
   *   - `thumbUrl` 자리에 `previewUrl` 을 그리면 목록이 `8 × 5.9MB` 를 편다. **지금 고치는
   *     버그가 8분의 1 크기로 돌아오는 것**이다. ⭐ **장수 상한이 2026-09-23 에 생겨
   *     (`MAX_IMAGES_PER_JOB` = 15) 그 최악이 15장에서 멈춘다.** ⛔ 그래도 괜찮다는 뜻이
   *     아니다. `15 × 5.9MB` 는 여전히 목록 한 화면이 감당할 양이 아니다.
   *   - `previewUrl` 자리에 `thumbUrl` 을 그리면 320px 를 404pt 로 늘린다. 그 화면(`A-51`)이
   *     하는 일이 **누끼 가장자리 판정**이고 그 판정 위에 장당 ₩500 을 쓸지가 걸려 있다.
   *     **흐린 그림을 보고 돈 쓸지를 고르게 된다.**
   *
   * ⚠️ **`previewUrl` 과 독립이다. 한쪽만 `null` 일 수 있다.** 판이 둘이면 R2 put 도 둘이라
   * 하나가 성공하고 하나가 실패할 수 있고, **그때도 장은 `DONE`** 이다(축소 실패로 누끼 결과를
   * 버리지 않는다. 8장 중 1장 규칙과 같은 성격이다). ⛔ **한쪽이 없다고 둘 다 감추지 마라.**
   * 있는 것을 안 쓰는 것뿐이다.
   *
   * ⛔ **이 두 칸을 `job-rules.ts` 의 판정 입력으로 쓰지 마라.** 서버가 `toImageView` 로 만든
   * 모양을 그대로 `canRemakeImage` 에 넘기는 자리가 있는데(`apps/api/src/routes/jobs.ts`),
   * **그 자리는 축소본을 모른다.** 판정이 이 칸을 보면 같은 장이 화면과 서버에서 다르게
   * 판정된다. 이 두 칸은 **그리는 데만 쓴다.** */
  thumbUrl: string | null;
  /** **③ 합성·배치를 거친 산출물** 주소(`docs/TEMPLATE.md` 0.1 의 ③ · 2장 B 상세 860 × 860).
   *
   * `/api/jobs/<jobId>/images/<imageId>/output?v=<providerCallCount>&kind=composed`
   *
   * ⭐ **`size` 와 `kind` 를 한 칸에 뭉치지 않는다.** `size` 는 **같은 그림의 작은 판**이고
   * `kind` 는 **다른 산출물**이다. 뭉치면 나중에 "합성본의 축소본"을 말할 수 없다.
   * 나머지는 `previewUrl` 과 같은 규약이다 — **같은 라우트**, 같은 `?v=`, 같은 소유자 검사,
   * 같은 `cache-control`. 경로를 가르지 않는 근거는 `previewUrl` 주석에 있고 여기 복사하지
   * 않는다.
   *
   * ⚠️ **지금 `kind=composed` 와 `size=` 를 함께 보내는 자리는 없다. 조합을 열지 마라.**
   * 합성본은 **860 × 860 PNG 라 디코드해도 2.96MB** 이고, 축소본이 생긴 이유
   * (3024 × 3350 → 40.5MB, 24.1)가 여기서는 성립하지 않는다. ⛔ 모르는 조합은 원본으로
   * 흘려보내지 말고 `size` 오타와 **같은 자리에서 400 으로 끊는다.**
   *
   * ⚠️ **`null` 의 뜻이 넷이다.** 앞 셋은 `previewUrl` 과 같은 결이다. ① 이 회차 전에
   * 만들어진 장 ② 합성이 실패한 장(**누끼는 그때도 `DONE`**) ③ 다시 만든 뒤 합성이 못
   * 따라온 장. ④ **셀러가 배경을 바꾼 뒤 아직 그 배경으로 합성하지 않은 장**(2026-09-20 에
   * 생긴 갈래다. 아래 참고).
   *
   * ⭐ **④ 는 앞 셋과 성격이 다르다. ② 와 뭉개지 마라.** ①②③ 은 전부 **우리 쪽 실패이거나
   * 우리가 아직 안 한 일**이다. ④ 만 **셀러가 방금 한 편집의 결과**다. 셀러가 한 일은 팔레트
   * 에서 색을 고른 것 하나인데 그 순간 그 작업의 합성본이 전부 조회에서 빠진다. **화면이 ②
   * 와 다르게 그리고 싶어질 수 있는 유일한 갈래가 이것이다.** ② 에 대해 우리가 할 말은
   * "만들다 실패했다"이고 ④ 에 대해 할 말은 "당신이 바꾼 배경으로는 아직 안 만들었다"라서,
   * 같은 빈 자리여도 셀러가 다음에 할 일이 다르다. ⚠️ **다만 이 칸 하나로는 둘이 갈리지
   * 않는다.** 서버는 어느 쪽인지 말해 주지 않고, 화면이 ④ 를 아는 길은 **자기가 방금
   * `PATCH` 로 배경을 바꿨다는 사실**뿐이다. 그 자리에 무슨 문구를 두는지는 `design-manager`
   * 소관이고, 되찾는 길이 아직 없다는 것은 `docs/DATA_DICTIONARY.md` 25.3a 에 미결로 있다.
   *
   * 존재 판정은 `previewUrl` 과 같은 방식이다. D1 이 아니라 R2 목록이고, **키가 파생돼
   * 낡은 합성본은 조회 대상에서 저절로 빠진다**(24.5 · 24.6). ⚠️ **그 파생의 입력이
   * `outputKey` 와 배경색 둘이다**(`composedKeyFor` 의
   * `jobs/<jobId>/composed/detail/<배경색>/<결과물 파일명>`). **한쪽만 적으면 다음 사람이
   * 갈래를 반만 읽는다.** `outputKey` 만 보면 **다시 만들기**로 낡는 ③ 만 읽히고 **배경
   * 변경**으로 낡는 ④ 가 안 읽힌다. 배경은 860 × 860 안에 **구워져** 들어가 바깥에서
   * 갈아끼울 수 없고, 주소의 `?v=` 는 `providerCallCount` 라 배경을 바꿔도 글자가 안 바뀐다.
   * **키가 그 차이를 아는 유일한 자리다.**
   *
   * ⛔ **폴백이 없다. `outputUrl` 로 떨어지지 마라.** `previewUrl` 쪽 이행기 폴백과 **성격이
   * 다르다.** 그쪽은 같은 그림을 틀린 크기로 그리는 **품질 문제**지만, 여기서 ② 를 내주는 것은
   * **아직 안 만든 것을 산출물이라고 부르는 것**이다(`docs/TEMPLATE.md` 0.1: "③ 이 설 때까지
   * ② 의 결과를 산출물이라고 부르지 않는다"). 없으면 `null` 이고, 자리를 비우든 자리표시를
   * 그리든은 화면 쪽 판단이다.
   *
   * ⛔ **`outputUrl` 을 이 값으로 바꾸지 마라.** 그 칸은 **내보내기·`detail-crop` 이 쓰는 누끼
   * 원본**이고 `detail-crop` 이 원본 해상도를 쓴다. 두 칸은 다른 단계의 물건이다.
   *
   * ⚠️ **대표이미지(산출물 A, 1000 × 1000)는 이 칸으로 오지 않는다.** 그것은 **작업당 하나**라
   * 자리가 `DetailJobView` 지 장마다 붙는 이 칸이 아니다. 그래서 이 칸은 산출물이 둘이 돼도
   * 쪼갤 일이 없다. ⛔ **그때 `kind` 값으로 `thumbnail` 을 쓰지 마라** — `imaging` 의
   * `OutputKind.thumbnail` 은 대표이미지 1000 × 1000 인데 이 라우트의 `size=thumb` 는
   * 화면용 320px 다. **같은 낱말이 두 개념을 가리키게 된다**(25.4). */
  composedUrl: string | null;
  /** 큐·진행 화면·이력이 같은 이름을 말해야 셀러가 어느 사진인지 안다. */
  originalFilename: string | null;
  captureSource: CaptureSource;
  /** 재시도 횟수는 호출 횟수에서 파생한다 — 두 곳에 세지 않는다. */
  providerCallCount: number;
  /** **이 장 때문에 셀러가 부담하지 않는 금액.** 목업 `A-31b`·`A-25c` 의
   *  `이 장에서 돌아가는 금액 ₩500` 이 이 값이다. 만든 장은 `null` 이다.
   *  (`docs/DATA_DICTIONARY.md` 28.3 · 28.5 ②)
   *
   * ⛔ **`RefundStatementView.refundedAmount` 와 다른 칸이다.** 그쪽은 **실행된 환불**이고
   * 정본이 `RefundItem` 이다. 이쪽은 **자격**이고, 오늘은 `RefundItem` 행이 하나도 없다
   * (28.1). 이름이 두 글자 차이라 이 문단이 둘을 가르는 정본이다.
   *
   * **방식 중립이다.** `Refund.mode` 가 `PG_REFUND`(실제 환불)와 `CAPTURE_SHORTFALL`(매입을
   * 덜 함)을 한 표에 담는 것과 같은 이유로, 이 칸도 어느 쪽인지 말하지 않는다.
   *
   * **값은 저장된 스냅샷에서만 온다**(28.3 의 세 단. 그 장의 `BillingItem.unitPriceAmount` →
   * `Payment.unitPriceAmount` → `null`). ⛔ **`CUTOUT_UNIT_PRICE_KRW` 를 곱해 만들지 마라.**
   * 지금 앱이 `cutoutPrice(failed.length)` 로 그러고 있고 그것이 이 칸을 만드는 이유다
   * (14.3 ① · 15.5 규약 1).
   *
   * ⛔ **이 값을 장수로 곱하지 마라.** 합계는 **못 만든 장들의 이 값을 더해서** 만든다.
   * 곱하면 단가가 장마다 다른 작업(다시 만들기가 낀 작업)에서 틀린다. 더하는 자리는 한
   * 곳이고 `job-rules.ts` 의 `refundableTotal` 이다.
   *
   * ⚠️ **`null` 은 `0` 이 아니다.** "이 작업에 단가 스냅샷이 없다"이고, 곧 **청구된 적도
   * 앞으로 청구될 일도 없는 작업**이라는 뜻이다(28.3 ③). 무엇이라고 적을지는 화면 결정이다.
   *
   * ✅ **그 이행기는 끝났다 (2026-09-24 실측. 칸은 옵셔널로 둔다).** `DetailJobView` 를 만드는
   * 자리가 `apps/api/src/routes/jobs.ts` 의 `toView` **하나뿐이고**, 그것을 부르는
   * `loadJobView` 가 **늘 `settlement` 를 넘긴다**(`readRefundSnapshot`). 곧 **작업 조회 응답의
   * 장에는 이 칸이 항상 실려 온다.** ⛔ **그래도 필수로 바꾸지 않는다** — 판정만 하는 자리가
   * 이 칸 없이 `toImageView` 를 부르고(`isRemakable`), 필수로 조이면 **그 자리가 없는 값을
   * 지어내야 한다.** 아래 문단은 그 판단의 이력이라 지우지 않는다.
   *
   * ⚠️ **옵셔널인 것은 이행기라서다.** 서버가 아직 이 칸을 채우지 않는다(2026-09-21 기준).
   * **`undefined` 는 "서버가 아직 안 준다"이고 `null` 은 "서버가 모른다고 답했다"라서 뜻이
   * 다르다.** 다만 화면이 할 일이 같아서 `refundableTotal` 은 둘을 같이 센다. 서버가 전
   * 경로에서 채우면 필수로 바꾼다. ⚠️ **장을 싣지 않는 응답은 이 값을 계산할 필요가 없다** —
   * `JobListItemView` 에 같은 칸을 만들지 않는 것이 옵셔널의 또 다른 이유다. */
  refundableAmount?: number | null;
  /** **지금 이 장의 공급자 호출이 막혀 있는 이유.** `null` 이면 막히지 않았다.
   *  (`docs/DATA_DICTIONARY.md` 28.5 ③ · 28.8. 축 설명은 `RecallBlockReason` 주석)
   *
   * **되살리기(실패한 장)와 다시 만들기(만든 장) 둘 다에 걸린다.** 그래서 이름이 `remake` 가
   * 아니라 `recall` 이다(`jobProviderRecallCount` 가 같은 이유로 같은 낱말을 골랐다. D-62 가
   * 떼어내려는 낱말 겹침을 공유 코드 안에 새로 만들지 않는다).
   *
   * **왜 작업이 아니라 장에 다는가.** `PROVIDER_UNAVAILABLE`·`JOB_PROCESSING`·`JOB_DISCARDED`
   * 는 작업 전체에 걸리지만 **`SOURCE_GONE`·`SOURCE_TOO_LARGE` 는 장마다 갈린다.** 작업 칸으로
   * 두면 그 한 장 때문에 멀쩡한 나머지 장의 버튼까지 꺼진다. **8장 중 1장 규칙과 같은
   * 성격이다.**
   *
   * ⛔ **화면이 `errorCode` 로 이 값을 대신 계산하지 마라.** 공급자가 지금 살아 있는지를
   * 화면은 알 수 없다. 서버가 안 줄 때 쓰는 폴백은 `recallBlockOf` 한 곳이고, 그 함수는
   * **모르면 `null` 을 낸다**(장애를 추측하지 않는다).
   *
   * ✅ **이 칸도 이행기가 끝났다 (2026-09-24 실측).** 근거는 바로 위 칸과 같은 한 자리다
   * (`toView` 하나 · `loadJobView` 가 늘 넘긴다). 아래 문단은 이력이라 지우지 않는다.
   *
   * ⚠️ **옵셔널인 것은 `refundableAmount` 와 같은 이행기 사정이다.** 서버가 아직 채우지
   * 않는다(2026-09-21 기준). `undefined`(아직 안 준다)와 `null`(막히지 않았다)의 뜻이
   * 다르지만 **화면이 할 일은 `recallBlockOf` 가 흡수한다.** */
  recallBlockedReason?: RecallBlockReason | null;
  /** **이 장의 원본이 사라지는 날 = 재이행(다시 만들기) 기한의 끝.** 30일이다.
   *  (`JobImage.sourceRetentionExpiresAt`. `0011` · `DATA_DICTIONARY` 31.4 M-21)
   *
   * ⭐ **법무 `4.9.1`(2)㉡ 이 요구하는 판정의 입력이 이 칸이다.** 판정이 **「원본이 아직
   * 있는가」가 아니라 「그 장의 기한 안인가」**를 봐야 한다. 그래서 이 값이 화면까지 와야
   * 하고, 오지 않으면 앱은 원본 유무로 추측할 수밖에 없다. **그것이 정확히 금지된
   * 모양이다.** 쓰는 자리는 `retention.ts` 의 `isWithinSourceRetention()` ·
   * `sourceRetentionRange()` 와 `job-rules.ts` 의 `canRemakeImage`·`canRetryImage` 다.
   *
   * ⛔ **`DetailJobView.retentionExpiresAt` 과 다른 칸이다. 대신 쓰지 마라.** 그쪽은
   * **7일**이고 뜻이 **목록에 보이는 동안**이다(`JOB_LIST_VISIBLE_DAYS`). 이름이 둘 다
   * `retention` 이라 이 저장소에서 가장 헷갈리는 한 쌍이고, 섞으면 **23일이 조용히
   * 사라진다.** 개명이 필요한지는 `DATA_DICTIONARY` 32.5 에 미결로 있다.
   *
   * ⛔ **화면이 `createdAt` 에 30을 더해 이 값을 만들지 마라.** 값은 행에 박혀 있고
   * (18.2 의 관례) 상수가 바뀌어도 옛 행은 따라 움직이지 않는다. 더해서 만들면 **정책이
   * 바뀌는 날 화면이 행보다 늦거나 이른 날짜를 적는다.**
   *
   * ⚠️ **`undefined` 와 `null` 의 뜻이 다르다.** `refundableAmount`·`recallBlockedReason`
   * 과 같은 관례다.
   *   - `undefined` . **서버가 이 축을 아직 안 준다.** `0011` 적용 전이거나 그 라우트가
   *     아직 이 칸을 싣지 않는다. 판정은 **시간 항을 곱하지 않고 지금까지대로 간다.**
   *   - `null` . **서버가 "모른다"고 답했다.** 칸은 섰는데 그 행에 값이 없다.
   *     ⭐ **막는 쪽으로 떨어진다.** 모르는 채로 열어 주면 기한이 지난 원본에 공급자
   *     호출이 나간다(`isWithinSourceRetention` 머리말).
   *
   * **서버가 전 경로에서 채우면 필수로 바꾼다.** ⚠️ 장을 싣지 않는 응답은 이 값이 필요
   * 없어서 `JobListItemView` 에는 같은 칸을 만들지 않는다. 목록이 구간을 말해야 하면
   * `sourceRetentionRange()` 가 장들에서 만든다.
   *
   * ⭐ **이 칸은 혼자 서지 못한다. `DetailJobView.servedAt` 과 짝이다**(36장). 이 칸이
   * 실리는 순간부터 `isSourceRetentionExpired` 가 **`now` 없는 호출을 전부 막으므로**,
   * 시계를 함께 싣지 않으면 **모든 장의 다시 만들기·되살리기가 잠긴다.** */
  sourceRetentionExpiresAt?: D1DateTime | null;
  /** **셀러가 이 장을 `만들지 않기` 로 정한 시각.** `null` 이면 아직 안 골랐다.
   *  (`A-24e`. PRD `7.6.7d` · `docs/DATA_DICTIONARY.md` 34장)
   *
   * ⭐ **이 칸 하나가 `A-24e` 의 「이미 고른 장은 다시 묻지 않는다」를 만든다.** 회차가 끝날
   * 때마다 시트가 다시 서므로(PRD `7.6.7d` "한 번에 받아내지 않고 반복으로 받는다"),
   * **고른 장을 기억하는 자리가 없으면 셀러는 이미 답한 물음을 회차마다 다시 본다.**
   * 판정은 손으로 하지 말고 `job-rules.ts` 의 `needsRecallDecision` 을 쓴다.
   *
   * ⛔ **`다시 만들기` 쪽은 이 칸에 안 담긴다. 담을 칸이 없는 것이 맞다.** 그 선택은 장을
   * `대기` 로 되돌리는 것이라 **`status` 가 곧 기록**이고(`FAILED` 가 아니면 이 물음 자체가
   * 성립하지 않는다), 되돌린 장이 또 실패하면 **그때는 다시 물어야 한다.** 두 선택을 한
   * 칸에 담으면 그 재질문이 막힌다(32.2).
   *
   * ⛔ **`status` 나 `errorCode` 로 이것을 대신 읽지 마라.** `status` 는 **처리 축**
   * (`PENDING`/`PROCESSING`/`DONE`/`FAILED`)이고 `errorCode` 는 **공급자가 왜 실패했는가**
   * 다. 셀러의 결정은 그 둘과 직교하고, `errorCode` 에 얹으면 **그 코드표가 곧 과금·환불
   * 판정 기준**이라(`0003` 의 컬럼 주석) 돈의 판정이 셀러의 화면 조작으로 바뀐다.
   *
   * ⚠️ **이 값이 있는 장은 되살리기가 잠긴다**(`canRetryImage`). 셀러가 돌려받기로 한 장을
   * 우리가 다시 부르면 **원가가 나가고 돌아갈 금액의 근거가 흔들린다.**
   *
   * ⚠️ **옵셔널인 것은 `refundableAmount`·`recallBlockedReason` 과 같은 이행기 사정이다.**
   * 저장할 칸(M-24)이 **승인 대기**라 서버가 아직 안 준다(2026-09-24 기준).
   * `undefined`(서버가 아직 안 준다)와 `null`(안 골랐다)은 뜻이 다르지만 **화면이 할 일이
   * 같아서** 판정 함수가 둘을 같이 센다. ⛔ **그 사이에 시트가 같은 물음을 반복하는 것은
   * 버그가 아니라 이 칸이 아직 안 선 상태 그 자체다**(32.5). */
  recallDeclinedAt?: string | null;
}

export interface JobBlockView {
  /** 어느 채널의 블록 구성인가. 0003 이 `JobBlock` 에 채널을 박았다.
   *
   * **셀러가 끈 블록과 채널에 애초에 없는 블록은 다른 것이다.** 앞은 켤 수 있고 뒤는 켤
   * 자리가 없다. 화면이 둘을 같이 그리면 셀러가 켜 보고 아무 일도 안 일어나는 것을 겪는다.
   * 판단은 `channelHasBlock()` 이 한다.
   *
   * ⚠️ 지금 서버가 이 값을 채우지 않아 옵셔널이다(`apps/api/src/routes/jobs.ts` 의 `toView`).
   * 0003 적용과 그 라우트 수정 뒤에 필수로 바꾼다. 그때까지 읽는 쪽은
   * `channel ?? DEFAULT_SALES_CHANNEL` 로 읽는다. */
  channel?: SalesChannel;
  blockType: BlockType | string;
  sortOrder: number;
  enabled: boolean;
}

export interface JobSpecItemView {
  id: string;
  sortOrder: number;
  label: string;
  value: string;
  /** VLM 추출값을 그대로 확정하지 않는다는 규칙을 화면이 지키려면 출처가 행에 있어야 한다. */
  source: "SELLER" | "VLM_DRAFT";
}

/** 목록 한 줄이 말하는 "그 작업 이력의 최신 상태". 정본은 이벤트이고 이것은 사본이다. */
export interface JobActivityView {
  at: string;
  source: ActivitySource;
  summary: string;
}

export interface DetailJobView {
  id: string;
  status: DetailJobStatus;
  errorCode: JobFailureCode | null;
  /** 허용 편집 넷 중 상품명. VLM 초안이라 셀러가 반드시 고칠 수 있어야 한다. */
  productName: string | null;
  /** 허용 편집 넷 중 배경. 팔레트 정본은 `docs/TEMPLATE.md`. */
  backgroundColor: string;
  /** ⭐ **셀러가 이 작업의 상품을 두고 한 진술.** 안 고르셨으면 `null`.
   *  (`A-26c` · `W-26`. PRD `7.6.9b`)
   *
   * ⛔ **코드값 하나로 줄이지 마라.** 이 칸이 **시각과 문장까지** 싣는 것은 `A-26c` 배너가
   * **`고르신 것과 시각이 그대로 기록됩니다`** 라고 약속했기 때문이고, 화면이 그것을 되비칠
   * 수 없으면 **그 약속을 확인할 길이 없다.**
   *
   * ⛔ **`null` 을 「화장품이 아니다」로 읽지 마라.** 「안 고르셨다」이고, 그때도 상품명
   * 만들기는 선다(`productNameOffered` 주석). 두 상태를 뭉개면 **셀러가 하지 않은 진술을
   * 화면이 셀러 이름으로 적는다.**
   *
   * ⚠️ **옵셔널이다.** 옛 서버는 이 칸을 안 싣고, `undefined`(서버가 안 준다)와
   * `null`(서버가 「안 고르셨다」고 답했다)은 뜻이 다르다. 다만 화면이 할 일이 같아
   * `productNameOffered(view.productCategoryStatement ?? null)` 한 줄로 같이 본다. */
  productCategoryStatement?: ProductCategoryStatementRecord | null;
  /** ⭐ **우리가 내고 셀러가 고치는 상품 분류.** 아직 아무 값도 없으면 `null`.
   *  (`A-26c`·`A-26d`. `packages/shared/src/product-classification.ts` ·
   *  `docs/DATA_DICTIONARY.md` 37장)
   *
   * ⛔⛔ **바로 위 칸(`productCategoryStatement`)과 다른 축이다. 이름이 닮았을 뿐이다.**
   * 위는 **셀러가 하는 말**이고 **상품명 만들기를 막는 판정**이며, 이 칸은 **우리가 낸
   * 초안**이고 **아무것도 막지 않는다.** ⛔ **한 조건식에 둘을 함께 넣지 마라.** 법무가
   * 그것을 **막지 않는 축이 막는 축에 붙는 실제 경로**로 지목했다(`4.12.2`-㉤).
   *
   * ⛔ **글자만 싣지 마라.** `source` 가 함께 와야 화면이 **이 값은 우리가 넣은 것**이라고
   * 말할 수 있다(`G1-33`-ⓐ). 목업이 점선(초안)과 실선(셀러가 고친 값)을 가르는 근거가
   * 그 칸이다.
   *
   * ⚠️ **옵셔널이고, 당분간 `undefined` 만 온다.** 담을 칸이 없다(M-26 승인 대기).
   * `undefined`(서버가 이 축을 아직 안 준다)와 `null`(서버가 「아직 값이 없다」고 답했다)은
   * 뜻이 다르고, ⭐ **`null` 이 남을 수 있어야 하는 것이 법무 요구**다(`G1-33`-ⓔ).
   * ⛔ **화면이 둘을 뭉개 「없으면 초안을 그려 둔다」로 가지 마라.** 그러면 우리가 낸 적
   * 없는 값이 우리 이름으로 서고, 그 위에 셀러의 행위 기록이 쌓인다. */
  productClassification?: ProductClassificationRecord | null;
  origin: JobOrigin;
  /** 이어서 하기 짧은 코드. 접근 경로라 추측하기 어려운 값이어야 한다. */
  handoffCode: string | null;
  /** 지금 판 번호. **정본은 `JobVersion` 이고 이것은 사본이다.**
   *
   * 0003 이후 판 번호는 작업·채널당 1부터라 이 한 값으로는 채널이 여럿일 때 어느 채널의
   * 판인지 말할 수 없다. 채널이 하나인 동안은 기본 채널의 사본으로 읽고, 두 번째 채널이
   * 붙으면 채널별로 뽑아야 한다(`docs/DATA_DICTIONARY.md` 9장 M-1 의 남은 자리). */
  currentVersionNo: number | null;
  lastActivity: JobActivityView | null;
  images: JobImageView[];
  blocks: JobBlockView[];
  specItems: JobSpecItemView[];
  createdAt: string;
  updatedAt: string;
  /** ⭐ **이 응답을 만든 서버 시각.** 시간 축 판정에 넘기는 `now` 가 이 값이다.
   *  (`docs/DATA_DICTIONARY.md` 36장. `33.7` 급 1 의 둘째 줄이 요구한 칸)
   *
   * **왜 작업 뷰가 시계를 함께 실어야 하는가.** `isSourceRetentionExpired` 는
   * **`sourceRetentionExpiresAt` 이 실려 오는 순간부터 `now` 없는 호출을 전부 막는 쪽으로
   * 떨어뜨린다**(`job-rules.ts` 의 표 둘째 줄. "축은 섰는데 시계가 없다 → 막는다").
   * 그래서 기한 칸과 이 칸은 **같은 응답에 함께 서야 한다.** 한쪽만 서면
   * `/remake` · `/retry` 와 화면 버튼이 **모든 장에 대해** 막힌다.
   *
   * ⛔ **화면이 `Date.now()` 로 만들지 마라.** 기한 문자열은 SQL 이 만든 `D1DateTime`
   * (`YYYY-MM-DD HH:MM:SS`, UTC)이고 판정은 **사전순 비교로 돈다.** 파싱을 넣는 순간
   * Hermes(RN)와 Workers 가 같은 문자열을 다르게 읽는 창이 열린다(`retention.ts` 머리말).
   * ⛔ **ISO 8601 을 싣지 마라.** 모양이 섞이면 **11번째 글자에서 갈린다**
   * (`T`(0x54) > 공백(0x20)). ⭐ **틀리는 방식이 조용하다** — 날짜가 다르면 앞자리에서
   * 갈려 멀쩡히 돌고, **기한이 오늘인 장 하나만** 하루 일찍 막힌다(36.2).
   * ⛔ **기기 시계를 믿지 않는 것이 이 칸의 존재 이유 전부다.** 셀러가 시계를 앞당기면
   * 기한 지난 원본에 ₩500 짜리 공급자 호출이 나간다.
   *
   * ⛔ **`createdAt`·`updatedAt` 과 축이 다르다.** 그 둘은 **작업 행의 시각**이라 다음 회차에
   * 같은 값이 다시 오지만, 이 값은 **이 응답의 사실**이라 회차마다 다르다. 저장하거나
   * 캐시에 얹지 마라.
   *
   * ⚠️ **읽는 순간의 지금이 아니라 응답이 만들어진 순간이다.** 이름이 `now` 가 아니라
   * `servedAt` 인 이유가 그것이다. 화면을 열어 둔 채 30분이 지나면 이 값도 30분 낡는다.
   * **그 사이에 기한이 지나는 장은 다음 조회에서 막힌다.** 판정이 30일 축이라 그 지연이
   * 실무에서 문제가 되지 않지만, **이 값을 초 단위 판정에 쓰지 마라.**
   * ⚠️ **정확히는 「D1 의 `CURRENT_TIMESTAMP` 를 읽은 시각」이지 바이트가 나간 시각이
   * 아니다.** 차이는 수백 ms 이고 30일 축에서 무의미하다.
   *
   * ⚠️ **옵셔널인 것은 이행기라서다.** `sourceRetentionExpiresAt` 의 `undefined` 와
   * **짝이고 같은 회차에 끝난다.** 둘 다 없으면 옛 서버이고 판정은 지금까지대로 간다.
   * ⛔ **`| null` 을 붙이지 마라. 서버 구현이 `?? null` 로 접는 것도 막는다.** 저 두 칸의
   * `null` 은 **행에 값이 없다**는 실재하는 상태지만, 서버 시각은 **행이 아니라 그 요청의
   * 사실**이라 "서버가 답했는데 지금을 모른다"라는 상태가 없다. `null` 을 열면 화면이
   * **있지도 않은 갈래**를 하나 더 다뤄야 하고, 그 처리는 반드시 추측이 된다.
   *
   * **쓰는 법.** `canRemakeImage(img, job, job.servedAt)` ·
   * `canRetryImage(img, job, job.servedAt)` · `recallBlockOf(img, job, job.servedAt)`.
   * ⛔ **셋에 같은 값을 넘겨라.** 한쪽만 넘기면 **버튼은 사라지는데 시트가 아무 말도 못
   * 한다**(`33.7` 급 1 둘째 줄).
   *
   * ⚠️ **`JobListItemView` 에는 같은 칸을 만들지 않는다.** 목록은 장을 싣지 않아 시간 축
   * 판정을 부르는 자리가 없고, 시계를 두 자리에 두면 **화면이 어느 시계를 쓸지 고르게
   * 된다.** 목록에서 받은 시계로 상세를 판정하면 그것은 **목록을 연 시각**이다. 목록이
   * 기한 구간을 말하게 되는 날(`sourceRetentionRange()`)에는 행이 아니라 **`JobListView`
   * 봉투**에 같은 이름으로 둔다. 한 응답 안의 행마다 다른 지금은 없다. */
  servedAt?: D1DateTime;
  /** ⭐ **이 작업의 결과를 지금 받을 수 있는가.** 서버 판정을 그대로 싣는다.
   *  (`docs/DATA_DICTIONARY.md` 45장. 판정 정본 `result-retention.ts`. 2026-10-10 사용자 결정 · O-78)
   *
   * **왜 싣는가.** 기한이 지났을 때와 판정을 못 했을 때 서버는 둘 다 결과 주소 넷
   * (`outputUrl` · `composedUrl` · `thumbUrl` · `previewUrl`)을 `null` 로 비운다. 주소만 보고는
   * 둘이 갈리지 않고, 화면은 누른 뒤에야 409 `RESULT_RECEIVE_EXPIRED` · 500
   * `RESULT_RECEIVE_CHECK_FAILED` 로 알게 된다. 이 칸이 그것을 **누르기 전에** 말한다.
   *
   * **불변식.** 결과 주소가 걷혔다 ⇔ `state !== "OPEN"`. 서버가 같은 값 하나로 둘을 정한다.
   *
   * ⛔ **`UNKNOWN` 을 「기한이 지났다」로 그리지 마라.** 판정 값을 못 읽은 것이다
   * (`ResultReceiveView` 주석의 표).
   * ⛔ **`until` 과 `servedAt` 을 비교해 화면이 다시 판정하지 마라.** 이 칸은 받기 경로와 같은
   * 질의(`readResultReceiveState`)의 시계로 낸 답이고, `servedAt` 은 다른 질의의 시계다. 둘이 초
   * 경계에서 갈리면 화면과 서버가 다른 답을 낸다. 판정은 `state` 하나로 읽는다.
   * ⛔ **`until` 의 날짜를 직접 자르지 마라.** `resultReceiveLastKstDay(until)` 을 쓴다(하루 어긋남).
   *
   * ⚠️ **옵셔널인 것은 이행기라서다.** `undefined` 는 **옛 서버**이고 화면은 지금까지대로(누른
   * 뒤 409 · 500 으로 안다) 간다. 새 서버는 **항상 싣는다.** 판정을 못 읽은 회차도
   * `{ state: "UNKNOWN" }` 으로 싣는다.
   * ⛔ **`| null` 을 붙이지 마라. 서버가 `?? null` 로 접는 것도 막는다.** 「판정을 모른다」는
   * 이미 `UNKNOWN` 이라는 값이 있고, `null` 을 열면 같은 상태가 두 모양이 된다(`servedAt` 과
   * 같은 규율).
   *
   * 셀러 본인의 결제 시각에서 나온 값이고 소유자 검사를 지난 응답에만 실린다. ⛔ 로그에 싣지
   * 않는다(`docs/ERROR_LOGGING.md` §3-4-2). ⚠️ `JobListItemView` 에는 이 칸을 두지 않는다.
   * 목록이 싣는 결과 주소는 대표 축소본 하나(`JobListItemView.thumbUrl`)뿐이고, 서버가 같은 판정으로
   * 그 주소를 걷는다(`OPEN` 이 아니면 `null`). 목록 화면은 받기 기한을 말하지 않으므로 판정 값을
   * 따로 실을 자리가 없다(`docs/DATA_DICTIONARY.md` 52장). */
  resultReceive?: ResultReceiveView;
}

/** 목록 한 행. 상세를 다 싣지 않는다 — 행마다 재조회하면 N+1 이다. */
export interface JobListItemView {
  id: string;
  status: DetailJobStatus;
  productName: string | null;
  origin: JobOrigin;
  imageTotalCount: number;
  imageDoneCount: number;
  /** 셀러가 실제로 낸 금액. **정본은 청구 항목 합계다** — 작업 행에 캐시하지 않는다.
   *
   * ⚠️ **환불이 이미 빠진 값이다**(`Σ BillingItem.amount − Σ RefundItem.amount`).
   * 같은 이름의 `JobBillingSummaryView.billedAmount` 와 같은 값이고 같은 경고가 붙는다. */
  billedAmount: number;
  lastActivity: JobActivityView | null;
  createdAt: string;
  /** ⭐ **이 작업 한 줄에 그릴 대표 축소본 주소.** `A-10` 작업 줄의 썸네일 자리다.
   *  (`docs/DATA_DICTIONARY.md` 52장)
   *
   * **무엇을 가리키는가.** 그 작업의 장 가운데 `sortOrder` 가 가장 작은(작업 안에서 유니크라 동률이 없다)
   * `status = 'DONE' AND outputKey IS NOT NULL AND outputPurgedAt IS NULL` 장의 축소본이다.
   * 모양은 `JobImageView.thumbUrl` 과 **글자 하나 다르지 않다**:
   * `/api/jobs/:jobId/images/:imageId/output?v=<providerCallCount>&size=thumb`.
   * ⛔ **`?v=` 를 빼거나 다른 번호를 달지 마라.** 상세와 목록이 같은 장을 같은 주소로 불러야
   * 캐시가 하나로 돌고, 다시 만들기 뒤 5분 동안 옛 그림이 남지 않는다(`toImageView` 주석).
   *
   * **`null` 인 경우(서버가 답했고 그릴 것이 없다).**
   *   - 위 조건에 맞는 장이 없다(아직 못 만들었다 · 전부 실패 · 결과물을 지웠다).
   *   - 작업이 `DISCARDED` 다.
   *   - 받기 판정이 `OPEN` 이 아니다(`CLOSED` · `UNKNOWN`). 상세의 `closeResultUrls` 와 같은 규칙이고
   *     같은 판정 함수로 낸다. ⛔ 목록에서만 따로 판정 규칙을 적지 않는다.
   *
   * ⚠️⚠️ **상세와 다른 점 하나. 「있을 수도 없는 주소」다.** 상세의 `thumbUrl` 은 R2 `list` 로
   * 축소본 객체가 실제로 있을 때만 주소를 낸다. 목록은 작업마다 `list` 를 하면 N+1 이라 **D1 만으로
   * 후보를 정한다.** 그래서 축소본(`THUMB`) 객체가 없는 장이 첫 장이면 **주소가 나가도 404** 다.
   * 그런 장: 축소본 기능(2026-09-19) 이전에 만든 장 · 축소 호출이나 `put` 이 실패한 장 · 회차 예산이
   * 떨어져 축소를 못 한 장 · **긴 변이 `THUMB` 목표보다 작아 서비스가 `THUMB` 을 안 만든 장**
   * (그 장은 `PREVIEW` 사본만 있다. `lib/rendition.ts`). 목록과 이미지 로드 사이에 받기 기한이
   * 닫히면 409 · 500 도 온다.
   * ⛔ **화면은 로드 실패를 빈 체커로 받는다. 다른 장 · `size=preview` · 원본으로 다시 시도하지
   * 마라.** 다른 장이 있는지 목록은 모르고, 원본은 디코드 40MB 짜리를 36pt 자리에 꽂는
   * 2026-09-19 의 그 버그다. ⛔ 화면 헬퍼 `thumbUrlOf`(thumb → preview → output 폴백)에 넣지 마라.
   * 그 헬퍼는 `JobImageView` 용이고 이 칸에는 떨어질 다음 칸이 없다.
   *
   * ⛔ **원본(`sourceKey`)으로 폴백하지 않는다.** 원본을 작게 내주는 라우트가 없다.
   *
   * **보안.** 새 라우트가 아니다. 그 주소는 소유자 검사 · `denyResultReceive`(받기 기한)를 지나고,
   * 축소본은 승인 전에도 소유자에게 열린다(`lib/output-access.ts` 의 표). 목록이 주소를 내는 것으로
   * 열리는 문은 없다.
   *
   * ⭐ **opt-in 이다.** 서버는 목록 요청에 `thumbs=1`(`JobListQuery.thumbs: true` ·
   * `JOB_SEARCH_PARAMS.thumbs`)이 있을 때만 이 칸을 계산해 싣는다. 없으면 **칸을 아예 싣지 않고**
   * 추가 질의도 내지 않는다. 썸네일을 그리지 않는 목록 소비자(알림 폴링 · 월 요약)가 질의 둘을
   * 더 내지 않게 하려는 것이다.
   *
   * **세 값의 뜻.**
   *   - `undefined`: 옛 서버(이 칸을 모른다) **또는 thumbs 를 요청하지 않았다.**
   *   - `null`: thumbs 를 요청했고 서버가 답했는데 그릴 것이 없다(위 갈래).
   *   - 문자열: 그릴 주소.
   * 화면이 할 일은 앞의 둘 다 빈 체커라 `item.thumbUrl ?? null` 로 같이 본다. ⛔ `undefined` 를
   * 「그릴 것이 없다」로 단정하지 마라. 요청을 안 했을 수 있다. */
  thumbUrl?: string | null;
}

/** 찾기 결과는 `조건에 맞는 3건 · 전체 18건 중`을 함께 쓴다(PRD 7.12 12번).
 *
 * `totalCount` 는 찾기 조건을 걸지 않은 수다(지운 작업 · 목록 기한이 지난 작업만 뺀다. `DISCARDED`
 * 는 센다). 홈 `A-10` 내비의 `전체 n건` 도 **이 값 하나**를 쓴다. 같은 수를 두 칸으로 내리지 않는다
 * (`docs/DATA_DICTIONARY.md` 52.3). */
export interface JobListView {
  items: JobListItemView[];
  matchedCount: number;
  totalCount: number;
}

/** 타임라인 한 줄. 세 줄 고정(언제 / 무슨 일 / 어디서·상세). */
export interface JobEventView {
  id: string;
  occurredAt: string;
  source: ActivitySource;
  type: string;
  summary: string;
  detail: string | null;
  /** `수정함`만 있으면 되돌릴지 판단할 수 없다. */
  beforeValue: string | null;
  afterValue: string | null;
  /** 이 이력 줄의 금액. 표시용 사본이라 **청구 판정에 쓰지 않는다** — 정본은 청구 항목이다.
   *
   * ⚠️ **이름이 `cost` 지만 담기는 것은 셀러 청구가다**(장당 ₩500). provider 에 나가는
   * 원가(₩27)가 아니다. `JobEvent.costAmount` 컬럼과 짝이고 서버가 `cutoutPrice`·`remakePrice`
   * 로 채운다. **이 값을 원가로 읽어 마진을 집계하면 18배
   * 부풀려진다.** 이름을 바로잡는 것은 마이그레이션이라 따로 간다
   * (`docs/DATA_DICTIONARY.md` 14.7). */
  costAmount: number | null;
  versionNo: number | null;
}

/** 내보낸 판 한 줄. `W-50` 의 판 목록과 `W-40`·`A-32` 의 `현재 v2` 가 이 모양을 읽는다. */
export interface JobVersionView {
  id: string;
  /** 어느 채널 규격으로 렌더한 판인가. 0003 이 `JobVersion` 에 박았다. */
  channel: SalesChannel;
  /** 그때의 규격 판(`ChannelSpec.specVersion`). **렌더가 끝난 뒤에는 되찾을 수 없어**
   *  판을 만드는 순간에 적는다. 0003 이전에 쌓인 판은 `LEGACY_CHANNEL_SPEC_VERSION` 이다. */
  channelSpecVersion: ChannelSpecVersion;
  /** 판 번호. **작업당이 아니라 작업·채널당 1부터다.** 스마트스토어 v2 와 쿠팡 v2 는
   *  다른 판이라 `현재 v2` 를 적을 때 채널을 함께 말해야 한다. */
  versionNo: number;
  createdAt: string;
  createdVia: VersionTrigger;
  createdSource: "APP" | "WEB";
  fileCount: number;
  blockCount: number;
  note: string | null;
}

/** 내보낸 파일 한 줄. 목업 `W-40`·`A-52` 의 받을 파일 목록이 이 모양이다. */
export interface JobVersionFileView {
  id: string;
  sortOrder: number;
  blockType: BlockType | string;
  /** `detail-01-hero.jpg`. 이름 규칙은 채널이 정한다. 화면에서 조립하지 않는다. */
  fileName: string;
  contentType: string;
  byteSize: number;
  /** 렌더한 실제 픽셀 크기(`860 × 860`). 0004 가 `JobVersionFile` 에 박았다.
   *
   * **블록 종류에서 되짚지 않는다.** 되짚으면 채널 규격이 바뀔 때 옛 판에도 새 숫자가
   * 붙어 셀러가 받은 파일과 화면이 달라진다. `0` 은 0004 이전에 쌓였을 판 전용 자리이고
   * 화면은 `0` 을 크기로 적지 않는다(그런 행은 실제로 없다). */
  width: number;
  height: number;
}

/** 알림 한 줄(`A-30`·`W-60`). **두 줄이다.** 굵은 제목과 보조줄.
 *
 * 한 문자열로 합치지 않는 이유: 목록은 제목만 굵게 그리고 보조줄은 한 줄로 자른다.
 * 합치면 화면이 그 두 규칙을 문자열 자르기로 흉내내게 되고, 문구가 바뀔 때마다 어긋난다.
 * 0004 가 `Notification.title` 을 더했다.
 *
 * 읽음 상태는 앱·웹이 공유하므로 기기가 아니라 계정에 붙는다. 만료 시각은 화면에 적지
 * 않아(30일이 지나면 그냥 사라진다) 이 형태에 없다.
 *
 * ## 두 종류의 칸 (2026-10-31, DATA_DICTIONARY 43장)
 *
 * - **박힌 칸** `title` · `body`: 서버가 **이벤트 순간에** 쓴 글자(스냅숏). 뒤에 상품명을
 *   고쳐도 바뀌지 않는다. 장 단위 실패(`4번 사진 · …`)도 여기 박는다. 구조 칸을 두지 않는다.
 * - **조인 칸** `jobStatus` · `jobOrigin` · `exportSource`: 읽을 때 다른 표에서 **지금 값**을
 *   붙인다. 글자가 아니라 화면이 갈래를 고르는 재료다(누르면 갈 곳, surface 별 꼬리).
 *
 * ⛔ 조인 칸을 글자로 풀어 `body` 에 박지 않는다. 앱과 웹이 같은 행을 다른 꼬리로 그리는데
 * 글자에 박으면 한쪽 꼬리가 다른 화면에 샌다. */
export interface NotificationView {
  id: string;
  type: NotificationType;
  /** 굵은 제목 줄. */
  title: string;
  /** 보조줄. 제목과 같은 말을 다시 적지 않는다. */
  body: string;
  /** 눌렀을 때 갈 작업. 작업과 무관한 알림은 `null`. */
  jobId: string | null;
  createdAt: string;
  /** 안 읽었으면 `null`. */
  readAt: string | null;
  /** `LEFT JOIN DetailJob` 의 **지금** 상태. 누르면 갈 곳을 고른다 — 그새 파기된 작업
   *  (`DISCARDED`)을 결과 화면으로 보내지 않기 위해서다. `jobId` 가 `null` 이면 `null`.
   *  ⚠️ 알림이 생길 때의 상태가 아니다. 그건 `type` 이 말한다. */
  jobStatus: DetailJobStatus | null;
  /** `DetailJob.origin`. 웹 준비됨 줄의 `· {앱}에서 올린 작업` 꼬리. 작업이 없으면 `null`.
   *  `UNKNOWN` 은 그대로 내려가고 낱말로 풀지 않는 것은 화면(`originWord`)의 몫이다. */
  jobOrigin: JobOrigin | null;
  /** 내보낸 곳. **`type === "EXPORTED"` 인 행에서만 값이 있다.**
   *
   * 스키마에 칸이 없어 **`Notification.id` = 그 내보내기의 `JobEvent.id`** 로 맞춰 쓰고,
   * 읽을 때 `LEFT JOIN "JobEvent" e ON e."id" = n."id" AND e."type" = 'EXPORTED'
   * AND n."type" = 'EXPORTED'` 로 `e."source"` 를 붙인다(DATA_DICTIONARY 43.6). 짝이 없으면
   * `null` 이고, 화면은 그때 꼬리를 그리지 않는다(모르는 곳을 `앱` 이라 적지 않는다). */
  exportSource: ActivitySource | null;
  /** FAILED 행 보조줄 끝 ` · 다시 만들어도 더 내실 금액 없음` 꼬리를 붙일지(PRD 7.6.12).
   *  **조인 칸이 아니라 라우트가 읽는 순간 계산하는 파생 칸이다**(DATA_DICTIONARY 43.6).
   *
   *  `true` 조건(PRD 7.6.12 ⓒ 2026-10-31 정정, 법무 `L-F81` 1~2): `type === "FAILED"` 이고, 그 행이
   *  **장 단위 실패 행**(작업 전량 실패로 쓰인 행이 아님)이며, 그 작업의 지금 못 만든 장(`FAILED`)이
   *  **하나 이상이고 그 전부가** 다시 만들 수 있을 때만. 장 하나가 다시 만들 수 있다 = shared
   *  `recallBlockOf` 가 **`null`** 이고, 승인된 결제가 있고, `ImageRefund` 행이 없고(상태 무관),
   *  다시 만들지 않기로 하지 않았고, 원본이 지워지지 않았고, 보관 기한이 비어 있지 않을 때.
   *  **하나라도 막히면 `false`**. `"PROVIDER_UNAVAILABLE"` · 보관 기한 `NULL` · `ImageRefund` 있음 ·
   *  결제 없음은 모두 막힌 것이다(판정 정본: api `lib/notifications.ts` `readRemakeFreeJobs`).
   *  그 밖은 전부 `false`. 판정 조회가 실패해도 `false` — 지킬 수 없는 약속을 적지 않는다.
   *  ⚠️ 두 실패 행을 가르는 칸이 스키마에 없어, 작업의 지금 `status === "FAILED"` 면 전량 실패
   *  행으로 보고 `false` 로 둔다(43.6 근사. 틀려도 꼬리가 빠지는 쪽). */
  remakeFreeAvailable: boolean;
  /** 앱 EXPORTED 행 ` · 여기서 바로 받기` 꼬리를 붙일지(PRD 7.6.12). 라우트 파생 칸.
   *
   *  `true` 조건: `type === "EXPORTED"` 이고, 작업 `status !== "DISCARDED"` 이며, 결과 받기
   *  기한(결제한 날부터 1개월. `apps/api` `denyResultReceive` 계열과 **같은 판정**) 안.
   *  기한 판정을 못 했으면(`UNKNOWN` · 질의 실패) `false` — 기한 지남으로 단정하지도, 받을 수
   *  있다고 약속하지도 않는다. 그 밖은 `false`. */
  receivable: boolean;
}

/** `GET /api/notifications`. `expiresAt` 이 지나지 않은 행만, 최신순, 최대
 *  `NOTIFICATION_LIST_LIMIT` 줄.
 *
 * ⚠️ **`unreadCount` 는 `items` 를 센 값이 아니다.** 상한 밖의 안 읽은 행까지 센다(만료된 것은
 * 빼고). 벨 배지가 50에서 멈추면 그 숫자가 거짓말을 한다. */
export interface NotificationListView {
  items: NotificationView[];
  unreadCount: number;
}

/** `POST /api/notifications/:id/read`. 갱신된 줄 하나.
 *
 * **이미 읽은 줄을 다시 읽어도 `readAt` 을 덮지 않는다** — 처음 읽은 시각이 사실이다. 그래서
 * 같은 요청을 두 번 보내도 같은 응답이 온다. 남의 행 · 만료된 행 · 없는 행은 셋 다 404 로
 * 같게 답한다(남의 알림이 있는지 없는지를 응답으로 가르지 않는다). */
export interface NotificationReadResponse {
  notification: NotificationView;
  /** 이 요청 뒤의 안 읽음 수. 화면이 목록을 다시 받지 않고 배지를 고칠 수 있게. */
  unreadCount: number;
}

/** `POST /api/notifications/read-all`. 만료되지 않은 안 읽은 행 전부에 `readAt` 을 박는다.
 *  `updatedCount` 는 이번 요청이 실제로 바꾼 줄 수(이미 읽은 것은 세지 않는다). 이 요청 뒤
 *  안 읽음 수는 정의상 `0` 이라 싣지 않는다. */
export interface NotificationReadAllResponse {
  updatedCount: number;
}

/** 작업 하나에 든 비용. 화면 여러 곳이 같은 숫자를 말하도록 형태를 하나만 둔다. */
export interface JobBillingSummaryView {
  /** **셀러가 부담하는 금액. 환불이 이미 빠져 있다.**
   *
   * 식은 하나로 박혀 있다: **`Σ BillingItem.amount − Σ RefundItem.amount`**. 앞의 합을
   * **`billable` 로 거르지 않는다.** 거르면 `CAPTURE_THEN_REFUND` 정산에서 실패한 장의
   * 금액이 **애초에 더해지지 않았는데 뒤에서 또 빠져** 셀러가 낸 것보다 적게 나온다
   * (`billing-history.ts` 의 `netAmount` · `docs/DATA_DICTIONARY.md` 15.7).
   *
   * ⚠️ **아래 `refundedAmount` 를 여기서 또 빼지 마라. 이미 빠져 있다.** 이름이 `billed`
   * 라 "청구한 금액"으로 읽히는데 **환불을 뺀 뒤의 값**이고, 두 칸이 나란히 있어 화면이 한
   * 번 더 뺄 수 있는 자리다. 서버 쪽 짝인 `JobBillingSummaryRow.billedAmount` 에 같은 경고를
   * 적어 뒀다(`apps/api/src/lib/jobs.ts`). **환불 전 총액이 필요하면 이 칸이 아니라
   * `RefundStatementView.paidAmount` 다.** */
  billedAmount: number;
  /** 청구된 장수. `COUNT(BillingItem WHERE billable = 1)` 이고 곧 **성공한 공급자 호출의
   *  수**다. `JobImage` 행 수도 `providerCallCount` 합도 아니다. */
  billedQuantity: number;
  /** 돌아간 금액. **표시용이고 빼는 데 쓰는 값이 아니다.** 위 `billedAmount` 에서 이미
   *  빠졌다. 둘을 더하면 **환불 전 청구 합**이 된다. */
  refundedAmount: number;
  refundedQuantity: number;
  retryQuantity: number;
  paymentStatus: PaymentStatus | null;
}
